import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { IconBlock } from '@hqo/react-components-library/dist/molecules/iconBlock';
import {
  IconBlockContainer,
  PaymentMethodContainer,
  PaymentMethodTitle,
  ServiceDetails,
  StyledPaymentDetails,
  StyledSummarySubtitle,
  StyledSummarySubtitleCompact,
  SummaryContainer,
} from './styles';
import { formatDate } from 'utils/formatDate';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { Amenities } from 'components/amenities';
import { CancellationInformation, useCancellationPolicyText } from 'components/cancellation-information';
import React, { useMemo } from 'react';
import { TRANSACTION_TYPES } from 'shared/consts';
import { formatCurrency } from 'utils/formatCurrency';
import { formatSavedPaymentMethodLabelFromTransaction } from 'utils/formatSavedPaymentMethodLabelFromTransaction';
import { getReceiptInfoFromTransaction } from 'utils/getReceiptInfoFromTransaction';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';
import { useSelector } from 'react-redux';
import { ReceiptAdditionalInformation } from 'components/payment/receipt/receipt-additional-information';
import { ReceiptPaymentMethod } from '../receipt-payment-method';
import { PaymentDetailsItem } from './payment-details-item';
import { AddOnDto } from 'store/add-ons/types';
import { getPaymentDetailsLabels } from 'utils/getPaymentDetailsLabels';
import { getPaymentDetailsValues } from 'utils/getPaymentDetailsValues';
import { MultiDayIconsBlock } from 'components/payment/receipt/components/multiDayIconsBlock/multiDayIconsBlock';
import { OrderType } from 'store/cart/types';
import { VisitorsCount } from './visitors-count';
import { AttendeeType, SupportedPaymentMethods } from 'store/transactions/types';
import { getResourceTimezone } from 'utils/getResourceTimezone';
import { Resource } from 'store/resource/types';
import { selectDiscountByUuid } from 'store/resource/selectors';
import { selectResourcesDiscountByUuid } from 'store/resources/selectors';
import type {
  PaymentDetailsValues,
  PaymentDetailsLabels,
} from '@hqo/react-components-library/dist/payment/payment-details/payment-details.interface';
import { MappedBookingObject } from 'components/payment/receipt/components/swipe-receipt-content.interface';
import { CreditsPaymentDetails } from 'components/payment/checkout/components/credits-payment-details';

interface ReceiptSummaryProps {
  transaction: MappedBookingObject;
  isSwipeModalContent?: boolean;
  onCancelBooking?: VoidFunction;
  // RES-999: the routed booking-details page renders its own date/location/cancel-action header
  // card (see pages/booking-details/booking-details.tsx) that duplicates exactly what the
  // date + location IconBlocks (and the multiday variant, and the visitor count next to them)
  // show here. Everything else in this component — additional info, cancellation policy,
  // amenities, payment details/method — has no equivalent on that page and stays rendered as-is.
  // Defaults to false so every other caller (swipe modal, historical receipt, etc.) is unaffected.
  hideEventOverviewBlock?: boolean;
  // RES-999: the routed booking-details page renders this whole component inside its own card
  // container, so the "Details" section heading should read as a small grey label matching the
  // card's other rows (When/Location/Reserved by) rather than its own large bold heading.
  // Defaults to false so every other caller keeps the original look.
  useCompactSubtitle?: boolean;
  // RES-999: the 130px bottom clearance SummaryContainer normally reserves is for the fixed/sticky
  // action bar below the swipe modal / plain receipt content — there's no such bar once this is
  // embedded inside the booking-details card, so it'd just be dead space. Defaults to false so
  // every other caller is unaffected.
  removeBottomSpacing?: boolean;
  /*
   * RES-1367: the payment block as a COLUMN of the booking-details card rather than a run of a
   * receipt pane -- flush with the column it is in, on the card's own tighter rhythm, and headed by
   * a small grey label instead of a module heading. See `compactPaymentBlock` in ./styles.
   *
   * Opt-in like the hides above: everywhere else this block IS the page, and the receipt's own
   * gutter and spacing are what it should have.
   */
  compactPaymentDetails?: boolean;
  // RES-1316: opt-in, because only the booking-details page renders the add-ons section that
  // replaces these lines. The standalone and swipe receipts have no such section, so for them the
  // vendor rows must keep appearing here or they appear nowhere at all.
  hideVendorAddOns?: boolean;
  /*
   * RES-1367: the booking-details card renders these two itself, as a matching pair of collapsible
   * sections below its Reserved by/When/Location rows -- so left on here they would be a second
   * copy of each, further up the same page and in the wrong order.
   *
   * Opt-in, like the two above: for every other caller this component IS where those sections
   * appear, and hiding them by default would make them appear nowhere at all.
   */
  hideAdditionalInformation?: boolean;
  hideAmenities?: boolean;
  /*
   * RES-1367: drops the leading section label -- the literal "Details"
   * (getReceiptInfoFromTransaction returns that title for a resource booking).
   *
   * It earns its place on a receipt, where it is the heading of the whole thing. Inside the
   * booking-details card it is two thirds of the way down a card that already names the booking in
   * its own title, so it labelled nothing the reader could not already see. Opt-in, like the hides
   * above, because on every other caller it IS the heading.
   */
  hideSubtitle?: boolean;
  /*
   * RES-1367: the booking-details card renders the policy itself, below Amenities, where the design
   * puts it -- so here it would be a second copy, above the payment block instead of below the
   * sections. Opt-in for the same reason as the rest: everywhere else this is where it lives.
   */
  hideCancellationPolicy?: boolean;
}

const DEFAULT_AMOUNTS_TOTAL_DISCOUNT = 0;

export const ReceiptSummary = ({
  transaction,
  onCancelBooking,
  isSwipeModalContent = false,
  hideEventOverviewBlock = false,
  useCompactSubtitle = false,
  removeBottomSpacing = false,
  compactPaymentDetails = false,
  hideVendorAddOns = false,
  hideAdditionalInformation = false,
  hideAmenities = false,
  hideSubtitle = false,
  hideCancellationPolicy = false,
}: ReceiptSummaryProps) => {
  const intl = useIntl();
  const locale = useLocale();

  const buildingTimeZone = useSelector(selectBuildingTimeZone);
  const resourceTimezone = getResourceTimezone(transaction.details.resource as unknown as Resource, buildingTimeZone);
  const { formattedFullDate } = formatDate(transaction?.details.start_at, locale as string, resourceTimezone);
  const { capitalizedBrand, formattedExpMonth, formattedExpYear } = formatSavedPaymentMethodLabelFromTransaction(
    transaction?.payment_method,
  );
  const discountFromResourceState = useSelector(selectDiscountByUuid(transaction?.discount_uuid));
  const resourcesDiscountFromResourcesState = useSelector(selectResourcesDiscountByUuid(transaction?.discount_uuid));

  const currentDiscount = discountFromResourceState || resourcesDiscountFromResourcesState;
  const discountAmount = currentDiscount?.amount ?? 0;

  const currencyType = transaction.currency_type;
  const subtotal = transaction.total_price;
  const taxSummary = transaction.total_tax;
  const total = transaction.total_charge;
  const discountApplied = transaction.details.discount_applied;
  const addOns = transaction?.details?.add_ons;
  const attendees = transaction?.details?.attendees;
  const isMobileDevice = useIsSmallViewportWidth();
  const isCreditsPayment = transaction?.payment_method?.type === SupportedPaymentMethods.CREDITS;
  const cancellationPolicyText = useCancellationPolicyText(transaction);

  const visitorsCount = useMemo(() => {
    const visitors = attendees?.length ? attendees?.filter(attendee => attendee.type === AttendeeType.VISITOR) : [];

    return visitors.length;
  }, [attendees]);

  const sortedAddOns = useMemo<AddOnDto[]>(
    () => addOns?.sort((a, b) => (a?.quantity < b?.quantity ? 1 : -1)),
    [addOns],
  );
  /*
   * Which rows to PRINT. Vendor add-ons share this array with the legacy cart lines, so where a
   * caller also renders the add-ons section they would otherwise appear twice on one page.
   *
   * ⚠️ Separate from which rows to SUBTRACT, below, and the two answer differently: a caller that
   * still prints the vendor lines does not thereby put them back into the room-charge arithmetic.
   */
  const listedAddOns = useMemo<AddOnDto[]>(
    () => (hideVendorAddOns ? sortedAddOns?.filter(addOn => !addOn.catalog_item_uuid) : sortedAddOns),
    [sortedAddOns, hideVendorAddOns],
  );
  /*
   * Only the rows that are actually inside `total_price`. No room price is stored anywhere, so the
   * room charge is reconstructed by subtracting the add-ons from the total -- which is right for a
   * cart line, bought in the same checkout, and wrong for a vendor add-on, ordered afterwards and
   * billed separately by its provider. Subtracting one of those shows a room charge that is too low,
   * and a negative one once the order exceeds the room.
   *
   * A Prism or Nexudus booking carries no catalog item on any row, so every row still counts there
   * and the figure is unchanged.
   */
  const chargedAddOns = useMemo<AddOnDto[]>(
    () => sortedAddOns?.filter(addOn => !addOn.catalog_item_uuid),
    [sortedAddOns],
  );
  const addOnsTotalCost = chargedAddOns?.reduce((sum, addOn) => sum + addOn.price * addOn.quantity, 0);
  const resourcePrice = subtotal - addOnsTotalCost;
  const amountsTotalDiscount = currentDiscount ? DEFAULT_AMOUNTS_TOTAL_DISCOUNT : transaction?.total_discount;
  const hasPaymentMethod = transaction?.payment_method?.type;

  const paymentDetailsValues = getPaymentDetailsValues(
    resourcePrice || 0,
    total,
    subtotal,
    taxSummary,
    discountApplied,
    currencyType,
    locale as string,
    intl,
    discountAmount,
    isCreditsPayment,
  );

  const SummarySubtitle = useCompactSubtitle ? StyledSummarySubtitleCompact : StyledSummarySubtitle;

  const showAdditionalInformation =
    !hideAdditionalInformation &&
    (transaction?.type === TRANSACTION_TYPES.RESOURCES_BOOKING || transaction?.type === OrderType.RESOURCE_BOOKING) &&
    !!transaction?.details?.user_info?.length;
  const showCancellationPolicy = !hideCancellationPolicy && !!cancellationPolicyText;
  const showAmenities = !hideAmenities && !!transaction?.details?.resource?.features?.length;
  /*
   * RES-1367: a caller that hides every section inside this block used to leave an EMPTY
   * ServiceDetails behind -- zero-height, but still a child of SummaryContainer, which is a grid
   * with an 18px gap. So hiding everything bought an 18px band above the payment details rather
   * than nothing at all.
   */
  const hasServiceDetails =
    !hideSubtitle || !hideEventOverviewBlock || showAdditionalInformation || showCancellationPolicy || showAmenities;

  return (
    <SummaryContainer data-testid="historical-receipt-summary" removeBottomSpacing={removeBottomSpacing}>
      {transaction?.type !== TRANSACTION_TYPES.MEMBERSHIP && hasServiceDetails && (
        <ServiceDetails isSwipeModalContent={isSwipeModalContent}>
          {!hideSubtitle && (
            <SummarySubtitle>
              {intl.formatMessage({
                id: getReceiptInfoFromTransaction(transaction, locale as string, resourceTimezone).title,
              })}
            </SummarySubtitle>
          )}
          {!hideEventOverviewBlock && (
            <IconBlockContainer>
              {transaction?.isMultiday ? (
                <MultiDayIconsBlock transaction={transaction} />
              ) : (
                <IconBlock
                  icon="calendar-alt"
                  iconType="fal"
                  action={Boolean(onCancelBooking)}
                  actionText={intl.formatMessage({ id: 'cancel_booking' })}
                  onActionClick={onCancelBooking}
                  title={formattedFullDate}
                  subtitle={
                    getReceiptInfoFromTransaction(transaction, locale as string, resourceTimezone).eventBlockSubtitle
                  }
                />
              )}
              {Boolean(visitorsCount) && <VisitorsCount visitors={attendees} />}
              <IconBlock
                icon="location-dot"
                iconType="fal"
                title={transaction.vendor.name}
                subtitle={transaction.details.resource.name}
              />
            </IconBlockContainer>
          )}
          {showAdditionalInformation && <ReceiptAdditionalInformation formResponses={transaction?.details.user_info} />}
          {showCancellationPolicy && (
            <CancellationInformation cancellationRule={cancellationPolicyText} compact={useCompactSubtitle} />
          )}
          {showAmenities && (
            <Amenities
              amenitiesDescription={transaction?.details?.resource?.amenities_description}
              amenities={transaction?.details?.resource?.features}
              isMobileDevice={isMobileDevice}
              compact={useCompactSubtitle}
            />
          )}
        </ServiceDetails>
      )}
      {!!transaction?.total_price &&
        (isCreditsPayment ? (
          <CreditsPaymentDetails
            itemName={transaction.details.name}
            itemPrice={paymentDetailsValues.itemPrice}
            title={intl.formatMessage({ id: 'payment_details' })}
            compact={compactPaymentDetails}
          />
        ) : (
          <StyledPaymentDetails
            paymentItem={<PaymentDetailsItem addOns={listedAddOns} currencyType={currencyType} />}
            title={intl.formatMessage({ id: 'payment_details' })}
            paymentDetailsLabels={
              getPaymentDetailsLabels(transaction.details.name, intl, isCreditsPayment) as PaymentDetailsLabels
            }
            paymentDetailsValues={paymentDetailsValues as unknown as PaymentDetailsValues}
            $compact={compactPaymentDetails}
          />
        ))}
      {!!transaction?.total_price && hasPaymentMethod && (
        <PaymentMethodContainer $compact={compactPaymentDetails}>
          <PaymentMethodTitle>{intl.formatMessage({ id: 'payment' })}</PaymentMethodTitle>
          <ReceiptPaymentMethod
            title={intl.formatMessage(
              {
                id: 'saved_payment_method_option',
              },
              {
                cardBrand: capitalizedBrand,
                cardLastFour: transaction?.payment_method?.last_four,
                cardExpMonth: formattedExpMonth,
                cardExpYear: formattedExpYear,
              },
            )}
            paymentMethod={transaction?.payment_method.type?.toUpperCase()}
            creditsAmount={formatCurrency(
              transaction?.payment_method?.remaining_points || 0,
              transaction?.currency_type,
              locale as string,
            )}
            total={transaction?.total_price}
            amounts={{
              totalCharge: transaction?.total_charge,
              totalDiscount: amountsTotalDiscount,
            }}
          />
        </PaymentMethodContainer>
      )}
    </SummaryContainer>
  );
};
